import type { PbjModel } from "../../../utils/models";

// Mirror of backend/services/pbj_model/constants.py:19-26 — the CMS national-
// average snapshot from NH_StateUSAverages_Apr2026. Used by the Facility Inputs
// dialog's "Reset to defaults" button. No runtime data fetch — these only drift
// quarterly, and adding an endpoint to read 8 floats would be over-engineered.
// Keep this file in sync with the BE constants when the snapshot is refreshed.
export const NATIONAL_DEFAULTS = {
	pccPdpmAvg: 1.39169,
	natNursingCaseMixIndex: 1.39169,
	natReportedTotalHrd: 3.87157,
	natReportedRnHrd: 0.67932,
	natReportedWeekendHrd: 3.4346,
	natCaseMixTotalHrd: 3.84455,
	natCaseMixRnHrd: 0.67457,
	natCaseMixWeekendHrd: 3.41063,
} as const;

// First-time defaults mirroring the BE PbjModel.default() factory: a 3★ target, exact
// CMS scoring (zero safety buffers), and the standard 22.5h long-shift audit threshold.
const DEFAULT_TARGET_STARS = 3;
const DEFAULT_LONG_SHIFT_THRESHOLD_HRS = 22.5;

/**
 * Seed row for a department that has never persisted a PBJ Model config. Mirrors the BE
 * `PbjModel.default()` factory (constants.py) so the FE's first-render values match what the
 * server would write on first save. Built from NATIONAL_DEFAULTS so the CMS snapshot magic
 * numbers live in exactly one place. All operational overrides start null/false/0 — the scorer
 * falls back to computed values until an admin sets them.
 */
export const defaultPbjModel = (departmentId: string): PbjModel => ({
	departmentId,
	facilityId: null,
	// Explicitly null rather than omitted: `usePbjModelDraft`'s dirty check diffs over this row's
	// OWN keys, so an absent optional field would make edits to it invisible to Calculate.
	ccn: null,
	stateCode: null,
	stars: DEFAULT_TARGET_STARS,
	...NATIONAL_DEFAULTS,
	// false = the CMI pair above is the (defaulted) snapshot and CASPER may auto-populate it;
	// pinned true only once an admin edits the pair by hand.
	cmiManual: false,
	averageCensus: null,
	wageOverrideRn: null,
	wageOverrideCna: null,
	wageOverrideLpn: null,
	wageOverrideWeekendRn: null,
	wageOverrideWeekendCna: null,
	wageOverrideWeekendLpn: null,
	manualTotalTurnoverPct: null,
	manualRnTurnoverPct: null,
	manualAdminTurnoverCount: null,
	adminTurnoverNa: false,
	adminTurnoverInvalid: false,
	totalTurnoverNa: false,
	totalTurnoverInvalid: false,
	rnTurnoverNa: false,
	rnTurnoverInvalid: false,
	projectedScoreBufferPct: 0,
	bandRangeBufferPct: 0,
	longShiftThreshold: DEFAULT_LONG_SHIFT_THRESHOLD_HRS,
	targetPoints: null,
});
